import { Autocomplete, TextField, useTheme } from "@mui/material";
import * as React from "react";

import { FormattedDateTime } from "@/shared/datetime";
import { DatasetRecord } from "@/shared/domain/datasets";
import { useDatasetsQuery } from "@/shared/domain/datasets/hooks";
import { OrgRecord } from "@/shared/domain/orgs";

interface DatasetSelectorProps {
  datasetId: string | null;
  setDatasetId: (arg: string | null) => void;
  currentOrg: OrgRecord | null;
}

export const DatasetSelector: React.FC<DatasetSelectorProps> = ({
  datasetId,
  setDatasetId,
  currentOrg,
}) => {
  const theme = useTheme();

  const datasetsQuery = useDatasetsQuery({
    orgId: currentOrg?.org_id,
    query: { sort_by: "created", sort_direction: "DESC" },
  });

  const options: DatasetRecord[] = datasetsQuery.data ?? [];

  return (
    <Autocomplete
      id="available-datasets"
      options={options}
      fullWidth
      freeSolo
      value={datasetId}
      size={"small"}
      loading={datasetsQuery.isLoading}
      groupBy={() => "Recent Datasets"}
      getOptionLabel={(option) =>
        (option as DatasetRecord).dataset_id ?? option
      }
      renderOption={(props, option: DatasetRecord) => (
        <li style={{ display: "block" }} {...props}>
          <>
            <span style={{ display: "block" }}>
              {option.dataset_id}
              {option.name ? ` — ${option.name}` : ""}
            </span>
            <span
              style={{
                fontSize: "0.7rem",
                display: "block",
                color: theme.palette.text.secondary,
              }}
            >
              {option.created_by} on{" "}
              <FormattedDateTime value={option.created} time="none" />
            </span>
          </>
        </li>
      )}
      onChange={(_event, newValue: DatasetRecord | string | null) => {
        if (typeof newValue === "object") {
          setDatasetId(newValue?.dataset_id ?? null);
        } else {
          setDatasetId(newValue);
        }
      }}
      onInputChange={(_event, newInputValue, reason) => {
        // Ensures typed/pasted values are registered (see: ROBO-1370)
        if (reason === "input") {
          setDatasetId(newInputValue);
        }
      }}
      renderInput={(params) => <TextField {...params} placeholder="Dataset" />}
      componentsProps={{
        paper: {
          sx: {
            fontSize: "0.875rem",
          },
        },
      }}
      sx={{ width: "100%", maxWidth: 400 }}
    />
  );
};
